import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import useSchools from "MyCalendar/hooks/useSchools";
import useSchoolSelection from "../hooks/useSchoolSelection";
import { ACTIVE_MODE } from "../modules/SelectParticipantsConstants";

export const SelectParticipantsContext = createContext();

export const useSelectParticipantsContext = () => {
  const context = useContext(SelectParticipantsContext);
  if (!context) {
    throw new Error(
      "useSelectParticipantsContext must be used within a SelectParticipantsModal"
    );
  }
  return context;
};

const SelectParticipantsProvider = ({
  children,
  initialSelectedGradeList,
  initialSelectedCourseList,
  activeMode: initialActiveMode,
  enableMembersMode,
}) => {
  const [activeModeState, setActiveModeState] = useState(initialActiveMode);
  const [selectedGradeList, setSelectedGradeListState] = useState(
    initialSelectedGradeList
  );
  const [groupBy, setGroupBy] = useState(ACTIVE_MODE.CLASSES);
  const [selectedCourseList, setSelectedCourseListState] = useState(
    initialSelectedCourseList
  );
  const [searchText, setSearchTextState] = useState("");
  // School selection — shared across all entity modes.
  // null = "all schools" sentinel; a Set means a specific subset is selected.
  const { schools } = useSchools();

  const [selectedSchoolIds, setSelectedSchoolIds] = useState(null);

  const {
    onSchoolChange,
    isAllSchoolsSelected,
    selectedSchoolLabel,
  } = useSchoolSelection(schools, selectedSchoolIds, setSelectedSchoolIds);

  // Member mode state (only meaningful when enableMembersMode is true)
  const [selectedMemberIds, setSelectedMemberIds] = useState(new Set());
  const [excludedMemberIds, setExcludedMemberIds] = useState(new Set());
  const [directlySelectedMemberIds, setDirectlySelectedMemberIds] = useState(
    new Set()
  );
  const entityMemberIndexRef = useRef({});

  const activeMode = activeModeState;

  const setActiveMode = useCallback(newMode => {
    setActiveModeState(newMode);
    setSearchTextState("");
  }, []);

  const setSearchText = useCallback(text => {
    setSearchTextState(text);
  }, []);

  // Clean up member state for entity IDs that were removed from selection
  const cleanupRemovedEntityMembers = useCallback(
    removedEntityIds => {
      if (!enableMembersMode || removedEntityIds.length === 0) return;
      const allRemovedMembers = [];
      removedEntityIds.forEach(entityId => {
        const members = entityMemberIndexRef.current[entityId];
        if (members) {
          allRemovedMembers.push(...members);
          delete entityMemberIndexRef.current[entityId];
        }
      });
      if (allRemovedMembers.length === 0) return;
      const removedSet = new Set(allRemovedMembers);
      setSelectedMemberIds(prev => {
        const next = new Set(prev);
        removedSet.forEach(mid => next.delete(mid));
        return next;
      });
      setExcludedMemberIds(prev => {
        const next = new Set(prev);
        removedSet.forEach(mid => next.delete(mid));
        return next;
      });
    },
    [enableMembersMode]
  );

  // Wrap setSelectedGradeList to clean up member state on entity removal
  const setSelectedGradeList = useCallback(
    updater => {
      setSelectedGradeListState(prev => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        if (enableMembersMode) {
          const prevIds = new Set(_.map(prev, "id"));
          const nextIds = new Set(_.map(next, "id"));
          const removed = [...prevIds].filter(id => !nextIds.has(id));
          cleanupRemovedEntityMembers(removed);
        }
        return next;
      });
    },
    [enableMembersMode, cleanupRemovedEntityMembers]
  );

  // Wrap setSelectedCourseList to clean up member state on entity removal
  const setSelectedCourseList = useCallback(
    updater => {
      setSelectedCourseListState(prev => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        if (enableMembersMode) {
          const prevIds = new Set(_.map(prev, "id"));
          const nextIds = new Set(_.map(next, "id"));
          const removed = [...prevIds].filter(id => !nextIds.has(id));
          cleanupRemovedEntityMembers(removed);
        }
        return next;
      });
    },
    [enableMembersMode, cleanupRemovedEntityMembers]
  );

  const effectiveSelectedMemberIds = useMemo(() => {
    if (!enableMembersMode) return new Set();
    const effective = new Set(selectedMemberIds);
    excludedMemberIds.forEach(id => effective.delete(id));
    return effective;
  }, [enableMembersMode, selectedMemberIds, excludedMemberIds]);

  const effectiveSelectedCount = useMemo(
    () => effectiveSelectedMemberIds.size,
    [effectiveSelectedMemberIds]
  );

  const handleToggleMember = useCallback(
    memberId => {
      if (!enableMembersMode) return;
      if (excludedMemberIds.has(memberId)) {
        // Re-include previously excluded member
        setExcludedMemberIds(prev => {
          const next = new Set(prev);
          next.delete(memberId);
          return next;
        });
        return;
      }
      if (selectedMemberIds.has(memberId)) {
        // Exclude entity-sourced member
        setExcludedMemberIds(prev => new Set(prev).add(memberId));
        return;
      }
      // Directly select member
      setDirectlySelectedMemberIds(prev => new Set(prev).add(memberId));
      setSelectedMemberIds(prev => new Set(prev).add(memberId));
    },
    [enableMembersMode, excludedMemberIds, selectedMemberIds]
  );

  const handleRemoveMember = useCallback(
    memberId => {
      if (!enableMembersMode) return;
      if (directlySelectedMemberIds.has(memberId)) {
        setDirectlySelectedMemberIds(prev => {
          const next = new Set(prev);
          next.delete(memberId);
          return next;
        });
        setSelectedMemberIds(prev => {
          const next = new Set(prev);
          next.delete(memberId);
          return next;
        });
      } else {
        setExcludedMemberIds(prev => new Set(prev).add(memberId));
      }
    },
    [enableMembersMode, directlySelectedMemberIds]
  );

  const handleEntityMembersResolved = useCallback(
    (entityId, memberIds) => {
      if (!enableMembersMode) return;
      entityMemberIndexRef.current[entityId] = memberIds;
      setSelectedMemberIds(prev => {
        const next = new Set(prev);
        memberIds.forEach(id => next.add(id));
        return next;
      });
    },
    [enableMembersMode]
  );

  const getEntityCheckboxState = useCallback(
    entityId => {
      if (!enableMembersMode) return null;
      const members = entityMemberIndexRef.current[entityId];
      if (!members || members.length === 0) return null;

      const excludedCount = members.filter(id => excludedMemberIds.has(id))
        .length;
      if (excludedCount === 0) return "checked";
      if (excludedCount === members.length) return "unchecked";
      return "indeterminate";
    },
    [enableMembersMode, excludedMemberIds]
  );

  const activeModeSelectedCount = useMemo(() => {
    if (activeMode === ACTIVE_MODE.GRADES) {
      return _.size(_.uniqBy(selectedGradeList, "id"));
    }
    return _.size(_.uniqBy(selectedCourseList, "id"));
  }, [activeMode, selectedGradeList, selectedCourseList]);

  const isAllEmpty = useMemo(
    () =>
      _.isEmpty(selectedGradeList) &&
      _.isEmpty(selectedCourseList) &&
      (!enableMembersMode || effectiveSelectedMemberIds.size === 0),
    [
      selectedGradeList,
      selectedCourseList,
      enableMembersMode,
      effectiveSelectedMemberIds,
    ]
  );

  const handleClearAll = useCallback(() => {
    setSelectedGradeListState([]);
    setSelectedCourseListState([]);
    if (enableMembersMode) {
      setSelectedMemberIds(new Set());
      setExcludedMemberIds(new Set());
      setDirectlySelectedMemberIds(new Set());
      entityMemberIndexRef.current = {};
    }
  }, [enableMembersMode]);

  const handleClearActiveMode = useCallback(() => {
    if (activeMode === ACTIVE_MODE.GRADES) {
      setSelectedGradeList([]);
      return;
    }
    if (activeMode === ACTIVE_MODE.CLASSES) {
      setSelectedCourseList([]);
      return;
    }
    if (activeMode === ACTIVE_MODE.MEMBERS && enableMembersMode) {
      setSelectedMemberIds(new Set());
      setExcludedMemberIds(new Set());
      setDirectlySelectedMemberIds(new Set());
      entityMemberIndexRef.current = {};
    }
  }, [
    activeMode,
    enableMembersMode,
    setSelectedGradeList,
    setSelectedCourseList,
  ]);

  const handleResetToInitial = useCallback(() => {
    setSelectedGradeListState(initialSelectedGradeList);
    setSelectedCourseListState(initialSelectedCourseList);
    setSearchTextState("");
    setSelectedSchoolIds(null);
    setActiveModeState(initialActiveMode);
    if (enableMembersMode) {
      setSelectedMemberIds(new Set());
      setExcludedMemberIds(new Set());
      setDirectlySelectedMemberIds(new Set());
      entityMemberIndexRef.current = {};
    }
  }, [
    initialSelectedGradeList,
    initialSelectedCourseList,
    initialActiveMode,
    enableMembersMode,
  ]);

  const contextValue = useMemo(
    () => ({
      activeMode,
      setActiveMode,
      selectedGradeList,
      setSelectedGradeList,
      selectedCourseList,
      setSelectedCourseList,
      searchText,
      setSearchText,
      schools,
      selectedSchoolIds,
      setSelectedSchoolIds,
      onSchoolChange,
      isAllSchoolsSelected,
      selectedSchoolLabel,
      groupBy,
      setGroupBy,
      activeModeSelectedCount,
      isAllEmpty,
      handleClearAll,
      handleClearActiveMode,
      handleResetToInitial,
      // Members mode state
      enableMembersMode,
      selectedMemberIds,
      excludedMemberIds,
      directlySelectedMemberIds,
      effectiveSelectedMemberIds,
      effectiveSelectedCount,
      entityMemberIndexRef,
      handleToggleMember,
      handleRemoveMember,
      handleEntityMembersResolved,
      getEntityCheckboxState,
    }),
    [
      activeMode,
      setActiveMode,
      selectedGradeList,
      setSelectedGradeList,
      selectedCourseList,
      setSelectedCourseList,
      searchText,
      setSearchText,
      schools,
      selectedSchoolIds,
      setSelectedSchoolIds,
      onSchoolChange,
      isAllSchoolsSelected,
      selectedSchoolLabel,
      groupBy,
      setGroupBy,
      activeModeSelectedCount,
      isAllEmpty,
      handleClearAll,
      handleClearActiveMode,
      handleResetToInitial,
      enableMembersMode,
      selectedMemberIds,
      excludedMemberIds,
      directlySelectedMemberIds,
      effectiveSelectedMemberIds,
      effectiveSelectedCount,
      handleToggleMember,
      handleRemoveMember,
      handleEntityMembersResolved,
      getEntityCheckboxState,
    ]
  );

  return (
    <SelectParticipantsContext.Provider value={contextValue}>
      {children}
    </SelectParticipantsContext.Provider>
  );
};

SelectParticipantsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialSelectedGradeList: PropTypes.array,
  initialSelectedCourseList: PropTypes.array,
  activeMode: PropTypes.string,
  enableMembersMode: PropTypes.bool,
};

SelectParticipantsProvider.defaultProps = {
  initialSelectedGradeList: [],
  initialSelectedCourseList: [],
  activeMode: ACTIVE_MODE.GRADES,
  enableMembersMode: false,
};

export default SelectParticipantsProvider;
